import React, { createContext, useContext, useReducer, useCallback } from 'react';

// Create the context
const UploadContext = createContext(null);

// Initial state for the reducer
export const uploadManagerInitialState = { uploads: {}, folderRenames: {} };

// Reducer function for managing upload states
export function uploadManagerReducer(state, action) {
  switch (action.type) {
    case 'REGISTER_FOLDER_RENAME':
      return {
        ...state,
        folderRenames: {
          ...(state.folderRenames || {}),
          [action.payload.id]: {
            ...action.payload,
            status: 'pending'
          }
        }
      };
    case 'UPDATE_FOLDER_RENAME': {
      const existingRename = state.folderRenames?.[action.payload.id];
      if (!existingRename || ['completed', 'failed'].includes(existingRename.status)) return state;
      return {
        ...state,
        folderRenames: {
          ...state.folderRenames,
          [action.payload.id]: {
            ...existingRename,
            ...action.payload
          }
        }
      };
    }
    case 'SETTLE_FOLDER_RENAME': {
      const existingRename = state.folderRenames?.[action.payload.id];
      if (!existingRename) return state;
      return {
        ...state,
        folderRenames: {
          ...state.folderRenames,
          [action.payload.id]: {
            ...existingRename,
            ...action.payload,
            status: action.payload.success ? 'completed' : 'failed',
            settledAt: action.payload.settledAt || Date.now()
          }
        }
      };
    }
    case 'ACK_FOLDER_RENAME': {
      if (!state.folderRenames?.[action.payload.id]) return state;
      const folderRenames = { ...state.folderRenames };
      delete folderRenames[action.payload.id];
      return { ...state, folderRenames };
    }
    case 'START_UPLOAD':
      return {
        ...state,
        uploads: {
          ...state.uploads,
          [action.payload.id]: {
            ...action.payload,
            progress: 0,
            status: 'uploading',
            error: null
          }
        }
      };
    case 'START_BATCH':
      return {
        ...state,
        uploads: {
          ...state.uploads,
          [action.payload.id]: {
            ...action.payload,
            isBatch: true,
            progress: 0,
            status: 'uploading',
            error: null,
            files: action.payload.files || [],
            expanded: false
          }
        }
      };
    case 'START_SUPER_BATCH':
      return {
        ...state,
        uploads: {
          ...state.uploads,
          [action.payload.id]: {
            ...action.payload,
            isSuperBatch: true,
            progress: 0,
            status: 'uploading',
            error: null,
            batches: [],
            expanded: false,
            processedFiles: 0,
            completedBatches: 0
          }
        }
      };
    case 'ADD_SUB_BATCH':
      const superBatch = state.uploads[action.payload.superBatchId];
      if (!superBatch) return state;
      return {
        ...state,
        uploads: {
          ...state.uploads,
          [action.payload.superBatchId]: {
            ...superBatch,
            batches: [...(superBatch.batches || []), action.payload.subBatch]
          }
        }
      };
    case 'UPDATE_SUB_BATCH':
      const parentBatch = state.uploads[action.payload.superBatchId];
      if (!parentBatch) return state;
      return {
        ...state,
        uploads: {
          ...state.uploads,
          [action.payload.superBatchId]: {
            ...parentBatch,
            batches: parentBatch.batches.map(b =>
              b.id === action.payload.subBatchId
                ? { ...b, status: action.payload.status, progress: action.payload.progress }
                : b
            )
          }
        }
      };
    case 'UPDATE_SUPER_BATCH_PROGRESS':
      return {
        ...state,
        uploads: {
          ...state.uploads,
          [action.payload.id]: {
            ...state.uploads[action.payload.id],
            progress: action.payload.progress,
            processedFiles: action.payload.processedFiles,
            completedBatches: action.payload.completedBatches
          }
        }
      };
    case 'TOGGLE_SUPER_BATCH_EXPAND':
      return {
        ...state,
        uploads: {
          ...state.uploads,
          [action.payload.id]: {
            ...state.uploads[action.payload.id],
            expanded: !state.uploads[action.payload.id].expanded
          }
        }
      };
    case 'TOGGLE_SUB_BATCH_EXPAND':
      const targetBatch = state.uploads[action.payload.superBatchId];
      if (!targetBatch) return state;
      return {
        ...state,
        uploads: {
          ...state.uploads,
          [action.payload.superBatchId]: {
            ...targetBatch,
            batches: targetBatch.batches.map(b =>
              b.id === action.payload.subBatchId
                ? { ...b, expanded: !b.expanded }
                : b
            )
          }
        }
      };
    case 'UPDATE_BATCH_PROGRESS':
      return {
        ...state,
        uploads: {
          ...state.uploads,
          [action.payload.id]: {
            ...state.uploads[action.payload.id],
            progress: action.payload.progress,
            processedFiles: action.payload.processedFiles,
            currentBatch: action.payload.currentBatch
          }
        }
      };
    case 'TOGGLE_BATCH_EXPAND':
      return {
        ...state,
        uploads: {
          ...state.uploads,
          [action.payload.id]: {
            ...state.uploads[action.payload.id],
            expanded: !state.uploads[action.payload.id].expanded
          }
        }
      };
    case 'UPDATE_PROGRESS':
      return {
        ...state,
        uploads: {
          ...state.uploads,
          [action.payload.id]: {
            ...state.uploads[action.payload.id],
            progress: action.payload.progress,
            statusMessage: action.payload.statusMessage ?? state.uploads[action.payload.id]?.statusMessage,
            loadedBytes: action.payload.loadedBytes ?? state.uploads[action.payload.id]?.loadedBytes,
            totalBytes: action.payload.totalBytes ?? state.uploads[action.payload.id]?.totalBytes,
            updatedAt: action.payload.updatedAt ?? state.uploads[action.payload.id]?.updatedAt
          }
        }
      };
    case 'COMPLETE_UPLOAD':
      return {
        ...state,
        uploads: {
          ...state.uploads,
          [action.payload.id]: {
            ...state.uploads[action.payload.id],
            progress: 100,
            status: 'complete',
            skipped: action.payload.skipped === true,
            statusMessage: action.payload.statusMessage ?? state.uploads[action.payload.id]?.statusMessage,
          }
        }
      };
    case 'FAIL_UPLOAD':
      return {
        ...state,
        uploads: {
          ...state.uploads,
          [action.payload.id]: {
            ...state.uploads[action.payload.id],
            status: 'failed',
            error: action.payload.error || 'Upload failed'
          }
        }
      };
    case 'ADD_UPLOAD':
      return {
        ...state,
        uploads: {
          ...state.uploads,
          [action.payload.id]: {
            ...action.payload,
            status: action.payload.status || 'uploading',
            progress: action.payload.progress || 0,
            error: null
          }
        }
      };
    case 'UPDATE_UPLOAD':
      return {
        ...state,
        uploads: {
          ...state.uploads,
          [action.payload.id]: {
            ...state.uploads[action.payload.id],
            ...action.payload
          }
        }
      };
    case 'CLEAR_COMPLETED':
      const newUploads = { ...state.uploads };
      Object.keys(newUploads).forEach(key => {
        if (newUploads[key].status === 'complete') {
          delete newUploads[key];
        }
      });
      return { ...state, uploads: newUploads };
    
    case 'CLEAR_ALL': {
      // Some server-side work (notably large ZIP creation) is intentionally
      // durable. Closing the tray must neither cancel it nor discard the
      // complete tray record that its background monitor will keep updating.
      const retainedUploads = Object.fromEntries(
        Object.entries(state.uploads || {}).filter(([, upload]) => (
          upload?.status === 'uploading' && upload?.cancelOnClear === false
        ))
      );
      return { ...state, uploads: retainedUploads };
    }
    
    default:
      return state;
  }
}

// Provider component
export const UploadProvider = ({ children }) => {
  const [state, dispatch] = useReducer(uploadManagerReducer, uploadManagerInitialState);

  // Keep the old context method as a fail-closed compatibility shim. Its former
  // consumer was the retired global Exhibits viewer; current case uploads use
  // the authenticated s3_upload.php service instead.
  const startChunkedUpload = useCallback(async () => {
    throw new Error('The legacy Exhibits uploader has been retired. Upload files from a case document section.');
  }, []);

  // Function to clear completed uploads
  const clearCompletedUploads = useCallback(() => {
    dispatch({ type: 'CLEAR_COMPLETED' });
  }, []);

  const clearAllUploads = useCallback(() => {
    Object.values(state.uploads || {}).forEach((upload) => {
      if (upload?.status === 'uploading'
          && upload?.cancelUrl
          && upload?.cancelOnClear !== false) {
        fetch(upload.cancelUrl, {
          method: upload.cancelMethod || 'POST',
          credentials: 'include',
          keepalive: true
        }).catch((error) => {
          console.warn('Failed to cancel background operation:', error);
        });
      }
    });
    dispatch({ type: 'CLEAR_ALL' });
  }, [state.uploads]);

  // Function to toggle batch expansion
  const toggleBatchExpand = useCallback((batchId) => {
    dispatch({ type: 'TOGGLE_BATCH_EXPAND', payload: { id: batchId } });
  }, []);

  // Function to toggle super batch expansion
  const toggleSuperBatchExpand = useCallback((superBatchId) => {
    dispatch({ type: 'TOGGLE_SUPER_BATCH_EXPAND', payload: { id: superBatchId } });
  }, []);

  // Function to toggle sub-batch expansion
  const toggleSubBatchExpand = useCallback((superBatchId, subBatchId) => {
    dispatch({ type: 'TOGGLE_SUB_BATCH_EXPAND', payload: { superBatchId, subBatchId } });
  }, []);

  const value = {
    uploads: state.uploads,
    folderRenames: state.folderRenames,
    startChunkedUpload,
    clearCompletedUploads,
    clearAllUploads,
    toggleBatchExpand,
    toggleSuperBatchExpand,
    toggleSubBatchExpand,
    dispatch // Expose dispatch for batch operations
  };

  return (
    <UploadContext.Provider value={value}>
      {children}
    </UploadContext.Provider>
  );
};

// Hook for consuming the context
export const useUploads = () => {
  const context = useContext(UploadContext);
  if (!context) {
    throw new Error('useUploads must be used within an UploadProvider');
  }
  return context;
};
